import api from '../utils/api';

/**
 * Fraud Detection Service - Handles fraud alerts and detection
 */

export const fraudService = {
  /**
   * Get all fraud alerts (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getAllAlerts: async (filters = {}) => {
    try {
      const response = await api.get('/admin/fraud/alerts', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch fraud alerts');
    }
  },

  /**
   * Get alert by ID
   * @param {string} id - Alert ID
   * @returns {Promise}
   */
  getAlertById: async (id) => {
    try {
      const response = await api.get(`/admin/fraud/alerts/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch alert');
    }
  },

  /**
   * Get fraud alert statistics
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getAlertStats: async (filters = {}) => {
    try {
      const response = await api.get('/admin/fraud/alerts/stats', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch alert stats');
    }
  },

  /**
   * Update alert status
   * @param {string} id - Alert ID
   * @param {Object} updateData - Status, resolution, actionTaken
   * @returns {Promise}
   */
  updateAlertStatus: async (id, updateData) => {
    try {
      const response = await api.put(`/admin/fraud/alerts/${id}/status`, updateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update alert status');
    }
  },

  /**
   * Get fraud detection rules
   * @returns {Promise}
   */
  getFraudRules: async () => {
    try {
      const response = await api.get('/admin/fraud/rules');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch fraud rules');
    }
  },

  /**
   * Create fraud detection rule
   * @param {Object} ruleData - Rule data
   * @returns {Promise}
   */
  createFraudRule: async (ruleData) => {
    try {
      const response = await api.post('/admin/fraud/rules', ruleData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create fraud rule');
    }
  },

  /**
   * Update fraud detection rule
   * @param {string} id - Rule ID
   * @param {Object} ruleData - Updated rule data
   * @returns {Promise}
   */
  updateFraudRule: async (id, ruleData) => {
    try {
      const response = await api.put(`/admin/fraud/rules/${id}`, ruleData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update fraud rule');
    }
  },

  /**
   * Delete fraud detection rule
   * @param {string} id - Rule ID
   * @returns {Promise}
   */
  deleteFraudRule: async (id) => {
    try {
      const response = await api.delete(`/admin/fraud/rules/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete fraud rule');
    }
  },

  /**
   * Manual fraud check
   * @param {Object} checkData - Check type, userId, orderId, etc.
   * @returns {Promise}
   */
  manualFraudCheck: async (checkData) => {
    try {
      const response = await api.post('/admin/fraud/check', checkData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to perform fraud check');
    }
  },

  /**
   * Export fraud alerts to CSV
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  exportAlerts: async (filters = {}) => {
    try {
      const response = await api.post('/admin/fraud/export', filters, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export alerts');
    }
  }
};

